import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import "./CSS/Login.css";
import { backend_url } from '../App';
import { useAdminMode } from '../context/AdminModeContext';
import { getHomePath } from '../utils/menuAccess';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]       = useState('');
  // arrived here because the 8-hour login ran out
  const expired = new URLSearchParams(window.location.search).get('expired') === '1';
  const [showForgot, setShowForgot] = useState(false);
  const navigate = useNavigate();
  const { setMode } = useAdminMode();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await axios.post(`${backend_url}/api/admin/login`, {
        username,
        password,
      });

      const { token, role, name, permissions } = response.data;
      sessionStorage.setItem("adminToken", token);
      sessionStorage.setItem("adminRole",  role);
      sessionStorage.setItem("adminName",  name);
      // Custom menu access; when absent the role's default menus apply
      if (Array.isArray(permissions)) {
        sessionStorage.setItem("adminPermissions", JSON.stringify(permissions));
      } else {
        sessionStorage.removeItem("adminPermissions");
      }

      // Land on the first page this user can access (Boutique dashboard if allowed)
      const home = getHomePath();
      setMode(home.startsWith("/boutique") ? "boutique" : "retail");
      navigate(home);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid username or password");
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Decorative brand panel */}
        <div className="login-brand-panel">
          <div className="login-brand-badge">✨ Admin Suite</div>
          <div className="login-brand-icon">🧵</div>
          <h1 className="login-brand-title">Aavryaa FabHub</h1>
          <p className="login-brand-text">
            Manage products, stock, orders and your team — all in one place.
          </p>
          <ul className="login-brand-points">
            <li>📦 Real-time stock control</li>
            <li>🧾 Effortless order tracking</li>
            <li>👥 Role-based team access</li>
          </ul>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <div className="login-icon">🔒</div>
          <h2>Admin Login</h2>
          <p className="login-subtitle">Sign in to access the Aavryaa admin panel</p>
          {expired && !error && (
            <p className="login-expired">⏰ Your session expired — please log in again. Unsaved changes may need to be redone.</p>
          )}
          {error && <p className="login-error">{error}</p>}

          <div className="login-input-group">
            <label>Username</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">👤</span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
              />
            </div>
          </div>

          <div className="login-input-group">
            <label>Password</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">🔑</span>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <span
                className="login-input-eye"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>

          <button type="submit" className="login-button">Login</button>

          <button
            type="button"
            className="login-forgot-link"
            onClick={() => setShowForgot(true)}
          >
            Forgot Password?
          </button>

          <p className="login-footer-text">
            🔐 Secured admin access · Aavryaa FabHub © {new Date().getFullYear()}
          </p>
        </form>
      </div>

      {showForgot && (
        <div className="login-forgot-overlay" onClick={() => setShowForgot(false)}>
          <div className="login-forgot-modal" onClick={(e) => e.stopPropagation()}>
            <div className="login-forgot-icon">✉️</div>
            <h3>Forgot Password?</h3>
            <p>
              Self-service password reset isn't available here. Please contact an
              Admin — they can reset your password from <strong>User Management</strong>.
            </p>
            <button className="login-forgot-close" onClick={() => setShowForgot(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
